/* **The app's one segmented control** — a sunken groove with the chosen option lifted out of it.
 *
 * A stylesheet with no component, the same shape as `emberButton.module.css` and
 * `photoBox.module.css` beside it: there is no behaviour here, and the two surfaces that draw it
 * hang it on different elements (`/raporty` on `<button>`s inside a `role="group"`, `/konto` on
 * native radios inside a `<fieldset>`), so a component would have to take the element as a prop.
 *
 * Promoted out of `pages/reports/ui/canon.module.css` on 18 September 2026, on its second consumer:
 * the account screen's Aplikacja tab, whose three rows are the same control. **Every declaration
 * below is that file's, moved unchanged, but for `.segOption`'s `position: relative`, which the
 * account screen's radio overlay needs** — read its header before touching one. It records that
 * this vocabulary was transcribed correctly once, re-drawn from scratch with different numbers a
 * second time, and that the owner refused the result on 17 September 2026. The mockup for `/konto`
 * draws the same groove at the same 3px padding, radius 11 and radius-8 option, so there is nothing
 * to reconcile — which is exactly why a second transcription would have been the wrong move.
 *
 * ── The chosen option is NEUTRAL ────────────────────────────────────────────────────────────
 * A raised card-coloured pill under `--shadow-card`, never the flame gradient. The gradient belongs
 * to the weekday chip and the toggle; an earlier pass filled the segment with it and it was wrong.
 *
 * ── What the surface declares ───────────────────────────────────────────────────────────────
 * Where the control stands, and nothing this file states. A declaration written on both sides is
 * settled by CHUNK ORDER, which differs between the dev server and the build.
 *
 * Every colour, space, radius and type value is a token; heights are layout. */

.seg {
  display: inline-flex;
  flex: none;
  gap: var(--space-3px);
  align-items: center;
  min-inline-size: var(--space-0);
  margin: var(--space-0);
  padding: var(--space-3px);
  background: var(--bg-inset);
  border: none;
  border-radius: var(--radius-11px);
}

.segOption {
  position: relative;
  display: inline-flex;
  gap: var(--space-7px);
  align-items: center;
  height: var(--h-seg-option);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
  cursor: pointer;

  /* Ink only. Nothing about the box is allowed to move — the options are content-width, so a
   * weight swap on selection would slide the two beside it on every press. */
  transition-duration: var(--dur-fast);
  transition-property: color, background-color;
  transition-timing-function: var(--ease);
}

.segOption:hover {
  color: var(--text);
}

.segOptionOn {
  color: var(--text);
  background: var(--bg-card);
  box-shadow: var(--shadow-card);
}

/* ── The INK cut, and the one surface that wears it ─────────────────────────────────────────
 * `/konto`'s billing-period switch, inside the Pro card (canon
 * `docs/design/subskrypcja-2026-09-21/`, variant A). The canon draws that control differently from
 * the groove above: it stands on `--bg-chip` with a hairline, its chosen option is the INK fill
 * rather than a lifted card, and it runs the full width of the card it sits in.
 *
 * That is not the "chosen option is neutral" rule being broken — the ink fill IS neutral, and the
 * rule it was written against is the flame GRADIENT. What it departs from is the raised-card
 * treatment, and the reason is the ground: a `--bg-card` pill on a `--bg-card` card is invisible,
 * and the two cards in this picker that draw it are exactly that.
 *
 * Declared AFTER `.seg`, `.segOption` and `.segOptionOn` at equal or higher specificity, so the
 * file's own order settles every one of them rather than the bundler's chunk order. Nothing here
 * is restated on the surface.
 *
 * The badge is the saving, and it is the one ember mark in the control: `--accent-text` at rest,
 * `--accent` on the filled option, where it stands on the ink rather than on the card. */
.segInk {
  display: flex;
  width: 100%;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
}

.segInk .segOption {
  flex: 1;
  justify-content: center;
  font-size: var(--fs-label);
}

/* The COMPACT cut — the plan overview's four switches (canon `plan-przeglad-2026-09-27`, its
 * `.presets button`): 26px options, 9px of side padding, 11.5px type. Added 27 September 2026 with
 * `SegmentedChoice`'s `size="compact"`; everything else about the control is the base cut's. */
.segCompact .segOption {
  height: 26px;
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
}

.segOptionInkOn {
  color: var(--on-ink);
  background: var(--text);
  box-shadow: none;
}

.segBadge {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-nav);
  color: var(--accent-text);
  font-variant-numeric: tabular-nums;
}

/* On the FILLED option the badge takes the option's own ink (owner, 22 September 2026), and the
 * measurements say it is not only a preference. The fill is `--text`. In Flame Dark that is white
 * and the raw `--accent` on it is 3.12:1, under AA for a 10px line. In FLAME it is worse than that:
 * `--accent` resolves to `--ink-900`, the mono ink, which is the same value `--text` is — the badge
 * was drawn at 1.00:1 on its own fill, which is to say not drawn at all. `--on-ink` is the ink that
 * fill already carries for its label: 19.67:1 in Flame Dark, 18.86:1 in Flame.
 *
 * Unselected, the badge keeps `--accent-text` above: that ground is the `--bg-chip` groove, where
 * the ember is the mark this control is for. */
.segOptionInkOn .segBadge {
  color: var(--on-ink);
}

.segOption:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── The radio cut ──────────────────────────────────────────────────────────────────────────
 * A surface may hang this on `<button>`s inside a `role="group"` (`/raporty`) or on native radios
 * inside `<label>`s (`/konto`), which is the house pattern `ScopeSwitch` and `RosterToolbar` use:
 * a radio group is one of the very few controls the platform already gets right.
 *
 * The input is taken off screen rather than `display: none`, which would take it out of the
 * accessibility tree and the group's whole keyboard contract with it. **That treatment is not
 * declared here.** It is `shared/ui/visuallyHiddenControl`, the app's one off-screen FOCUSABLE
 * control, and this file carried a byte-identical copy of it until 22 September 2026 — the exact
 * near-duplicate the styling rules exist to stop. The radio cut's consumers wear that class
 * directly, the way `BrandLoader` and `ExerciseAuthorMark` name two classes on one element;
 * `composes` is not used anywhere in this app and Stylelint does not know the property.
 *
 * `.segOption` is the positioned ancestor that rule's `position: absolute` resolves against, so
 * the input lands inside its own label rather than in the viewport's corner — see that file's
 * header for why a focusable control cannot take `visuallyHidden`'s `fixed`.
 *
 * The ring moves onto the label, because the label is not the focused element and `:focus-visible`
 * above therefore never matches on this cut. Matched on `input` rather than on a class of ours:
 * the class on that element belongs to the shared sheet now, and `.segOption` holds exactly one
 * input in this cut and none in the other. */
.segOption:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── The ink cut's own ring ─────────────────────────────────────────────────────────────────
 * The rings above are `--text`, and on this cut `--text` is also the CHOSEN option's FILL: the ring
 * and the thing it is meant to outline were the same colour in both themes, so the focused option
 * announced itself with a 2px band of its own fill and a keyboard user had nothing to follow.
 *
 * `--accent-text` rather than `--on-ink` or the raw `--accent`: the ring stands at
 * `outline-offset: 1px`, so what it is adjacent to on BOTH sides is the `--bg-chip` groove, not the
 * fill. Measured against that groove: the raw ember is 2.76:1 in Flame and the per-theme accent INK
 * is 3.95:1 there and 6.43:1 in Flame Dark, so only the second clears the 3:1 a focus indicator
 * owes. It is also already this control's own accent — the saving badge wears it at rest. Width and
 * offset are the app's, untouched.
 *
 * Declared last, at the same specificity as the two rules it narrows, so this file's own order
 * settles it rather than the bundler's chunk order. */
.segOptionInkOn:focus-visible,
.segOptionInkOn:has(.segInput:focus-visible) {
  outline-color: var(--accent-text);
}

.segIcon {
  display: block;
  flex: none;
  width: var(--sz-report-glyph);
  height: var(--sz-report-glyph);
}

